import { NodeKey, SerializedTextNode, TextNode } from "lexical"

import styles from "./styles.module.css"

export type MentionRecordType =
  | "User"
  | "Team"
  | "Household"
  | "Client"
  | "Prospect"
  | "Person"
  | "Business"
  | "Contact"
  | "Trust"
  | "CustodianAccount"
  | "FinancialAccount"
  | "InsurancePolicy"

export interface MentionableRecordMetadata {
  id: string
  type: string
  label: string
}

export interface MentionRecord {
  id: string
  label: string
  recordType: MentionRecordType
  mentionableRecord?: MentionableRecordMetadata
}

const MENTION_STYLES = {
  BASE: `${styles["mention-node"]} whitespace-nowrap font-medium text-tertiary-foreground border-b border-dashed border-border-accent transition-colors duration-200`,
  USER: `hover:text-foreground hover:border-foreground`,
  OTHER: `hover:text-foreground hover:border-foreground`,
}

export const USER_MENTION_CLASS = `${MENTION_STYLES.BASE} ${MENTION_STYLES.USER}`
export const OTHER_MENTION_CLASS = `${MENTION_STYLES.BASE} ${MENTION_STYLES.OTHER}`

export function getMentionClassForType(recordType: MentionRecordType | string): string {
  return recordType === "User" ? USER_MENTION_CLASS : OTHER_MENTION_CLASS
}

export function $createMentionNode(
  label: string,
  recordType: string,
  recordId: string,
  mentionableRecord?: MentionableRecordMetadata
): MentionNode {
  return new MentionNode(label, recordType, recordId, mentionableRecord).setMode("token")
}

interface SerializedMentionNode extends SerializedTextNode {
  recordType: string
  recordId: string
  mentionableRecord?: MentionableRecordMetadata
  type: "mention"
  version: 1
}

export class MentionNode extends TextNode {
  __recordType: string
  __recordId: string
  __mentionableRecord?: MentionableRecordMetadata

  static getType(): string {
    return "mention"
  }

  static clone(node: MentionNode): MentionNode {
    return new MentionNode(node.__text, node.__recordType, node.__recordId, node.__mentionableRecord, node.__key)
  }

  constructor(
    text: string,
    recordType: string,
    recordId: string,
    mentionableRecord?: MentionableRecordMetadata,
    key?: NodeKey
  ) {
    super(text, key)
    this.__recordType = recordType
    this.__recordId = recordId
    this.__mentionableRecord = mentionableRecord
  }

  createDOM(): HTMLElement {
    const dom = document.createElement("span")
    dom.className = getMentionClassForType(this.__recordType)
    dom.innerText = this.__text
    dom.setAttribute("data-record-type", this.__recordType)
    dom.setAttribute("data-record-id", this.__recordId)
    dom.setAttribute("data-mention", "true")
    dom.contentEditable = "false"
    return dom
  }

  isToken(): boolean {
    return true
  }

  isSegmented(): boolean {
    return false
  }

  exportJSON() {
    return {
      ...super.exportJSON(),
      recordType: this.__recordType,
      recordId: this.__recordId,
      mentionableRecord: this.__mentionableRecord,
      type: "mention",
      version: 1,
    }
  }

  static importJSON(serializedNode: SerializedMentionNode): MentionNode {
    const node = $createMentionNode(
      serializedNode.text,
      serializedNode.recordType,
      serializedNode.recordId,
      serializedNode.mentionableRecord
    )

    node.setFormat(serializedNode.format)
    node.setDetail(serializedNode.detail)
    node.setMode(serializedNode.mode)
    node.setStyle(serializedNode.style)

    return node
  }
}
